import { StoreModel } from "./vault-parser/parser.type";
import { StoreModelNameByVaultCode, StoreModelNames, VaultCodeEnum } from "./type/dali.type";
import { UserStoreService } from "../store/user/user.store.service";
import { inject, Injectable, isDevMode } from "@angular/core";
import { TransactionStoreService } from "../store/transaction/transaction.store.service";
import { ScenarioStoreService } from "../store/scenario/scenario.store.service";
import { ImportStoreService } from "../store/import/import.store.service";
import { DataPluginStoreService } from "../store/data-plugin/data-plugin.store.service";
import { CalculationPastStoreService } from "../store/calculation-past/calculation-past.store.service";
import { CalculationTodayStoreService } from "@bitwarden/web-vault/app/services/store/calculation-today/calculation-today.store.service";
import { ModelStoreAbstraction } from "../store/model.store.abstraction";
import { DaliService } from "./dali.service";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";
import { StoreData } from "@bitwarden/web-vault/app/models/store/store.data";
import { BudgetStoreService } from "@bitwarden/web-vault/app/services/store/budget/budget.store.service";
import { SavingGoalStoreService } from "@bitwarden/web-vault/app/services/store/saving-goal/saving-goal.store.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { ApmService } from "@elastic/apm-rum-angular";

@Injectable({
  providedIn: "root",
})
export class StoreLoaderService {
  private appState: AppStateStoreService = inject(AppStateStoreService);
  private daliService: DaliService = inject(DaliService);
  private userStore: UserStoreService = inject(UserStoreService);
  private transactionStore: TransactionStoreService = inject(TransactionStoreService);
  private scenarioStore: ScenarioStoreService = inject(ScenarioStoreService);
  private importStore: ImportStoreService = inject(ImportStoreService);
  private dataPluginStore: DataPluginStoreService = inject(DataPluginStoreService);
  private calculationPastStore: CalculationPastStoreService = inject(CalculationPastStoreService);
  private calculationTodayStore: CalculationTodayStoreService = inject(
    CalculationTodayStoreService,
  );
  private budgetStore = inject(BudgetStoreService);
  private savingGoalStore = inject(SavingGoalStoreService);
  private log: LogService = inject(LogService);
  private apmService: ApmService = inject(ApmService);
  protected exportedVault: Map<VaultCodeEnum, StoreData[]> = null;

  /**
   * True once the three stores that feed a view-readiness latch (calculationPast,
   * calculationToday, scenario — see {@link ViewReadinessService}) have had initialize() called in
   * {@link runTier2Init}. A tier-2 failure AFTER this point (budget/savingGoal) leaves every view
   * rendering normally, so it must NOT force a reload; only a failure before it strands the views.
   * Reset at the start of each run so a re-login (same tab, no reload) re-evaluates.
   */
  private calcStoresInitialised = false;

  /**
   * Tier 1. Blocks the UI (pizza loader): vault decrypt, ImportStore, UserStore.
   * Ends the overlay so the UI unlocks. Tier 2 then fires in the background.
   */
  async initialiseTier1() {
    this.appState.startProcess("store-initialisation", "Initializing GLOSS Vault...");
    const data = await this.daliService.initialise();

    if (isDevMode()) {
      this.exportedVault = data;
    }

    this.loadModels(data);
    // order matters, ImportStore must initialise before UserStore (no await — matches current code)
    this.importStore.initialize();
    await this.userStore.initialize();
    this.appState.isTier1Ready.set(true);
    this.appState.isBackgroundLoading.set(true);
    this.appState.endProcess("store-initialisation");
  }

  /**
   * Core tier-2 init sequence — background stores: DataPluginStore, TransactionStore,
   * CalculationPast, CalculationToday, Scenario, Budget. Throws on failure; the
   * fire-and-forget {@link initialiseTier2} wrapper catches it.
   */
  private async runTier2Init(): Promise<void> {
    this.calcStoresInitialised = false;
    await this.dataPluginStore.initialize();
    this.transactionStore.initialize();
    this.calculationPastStore.initialize();
    this.calculationTodayStore.initialize();
    this.scenarioStore.initialize();
    // Past this point every view-readiness store is wired, so a later throw no longer strands the
    // views — used to scope the failure-recovery prompt in initialiseTier2().
    this.calcStoresInitialised = true;
    // Budget and saving-goal are independent, post-view-readiness stores. Isolate each so a single
    // malformed record (BudgetStoreCollection.triggerChanges() builds a BudgetView per stored budget,
    // so one bad one throws) can't skip the other — previously a budget throw silently left saving
    // goals uninitialised (#5522). The views are already wired, so a failure here is reported (log +
    // APM) but not rethrown — keeping the "report, don't force-reload" contract from #5469 (a reload
    // wouldn't fix deterministic bad data anyway). NOTE: this does NOT make the failed store's own data
    // appear — a bad budget record still leaves budgetViews() empty (triggerChanges is all-or-none);
    // surfacing that as a per-view error state is #5521.
    this.initialiseIndependentStore(() => this.budgetStore.initialize(), "budget");
    this.initialiseIndependentStore(() => this.savingGoalStore.initialize(), "savingGoal");
    // Signals that initialize() was invoked — async enrichment/calc may still be running.
    this.appState.isTier2Ready.set(true);
    // Tier-2 wired successfully — clear the one-shot reload guard so a genuinely new failure later
    // in this tab (e.g. a same-tab re-login) still gets its own recovery prompt.
    this.appState.clearInitFailureReloadGuard();
  }

  /**
   * Runs an independent, post-view-readiness store's `initialize()` in isolation: a throw is reported
   * (log + APM) and swallowed so it can't abort the rest of {@link runTier2Init} (#5522). Only safe for
   * stores whose failure doesn't strand the views — the calc/scenario stores above must still propagate.
   */
  private initialiseIndependentStore(init: () => void, label: string): void {
    try {
      init();
    } catch (e) {
      this.log.error(`tier-2 ${label} init failed: ${e?.message ?? e}`);
      this.apmService.apm.captureError(e instanceof Error ? e : new Error(String(e)));
    }
  }

  /**
   * Tier 2 — background path (no full-screen overlay). Fired fire-and-forget from
   * bootstrap, so it MUST be self-contained: an uncaught throw here would be an unhandled
   * rejection and would silently abort the chain, stranding the bootstrap calc-readiness
   * await (PR #3708). On failure the calc stores may never have initialised, so
   * `isCalculationReady$` never fires and every view is stuck on its skeleton with no
   * recovery — `isStoreInitialised` has already latched true post-tier-1, so neither the
   * blocking overlay nor the rogue-process watchdog re-raises. Report to APM (nothing reads
   * `isTier2Ready`, so it isn't a recovery signal) and — only when the failure stranded the views
   * (i.e. before the calc/scenario stores were wired) — surface the refresh dialog so the user can
   * reload; the bootstrap await also has its own timeout. A later failure (budget/savingGoal)
   * leaves every view working, so it's reported but not forced to reload.
   */
  async initialiseTier2() {
    try {
      await this.runTier2Init();
    } catch (e) {
      this.log.error(`initialiseTier2 failed: ${e?.message ?? e}`);
      this.apmService.apm.captureError(e instanceof Error ? e : new Error(String(e)));
      this.appState.isTier2Ready.set(true);
      if (!this.calcStoresInitialised) {
        this.appState.promptRefreshAfterInitFailure();
      }
    }
  }

  loadModels(map: Map<VaultCodeEnum, StoreModel[]>) {
    for (const [daliType, models] of map.entries()) {
      const store = this.getStore(daliType);
      const modelName = this.getModelName(daliType);
      store.addStoreModels(modelName, models);
    }
  }

  /**
   * Re-initialises every store from models already loaded in memory (no vault re-decrypt).
   * Used after {@link clearStores} — e.g. purge-vault flow.
   */
  async initializeStores(): Promise<void> {
    // order matters, ImportStore must initialise before UserStore (no await — matches current code)
    this.importStore.initialize();
    await this.userStore.initialize();
    await this.dataPluginStore.initialize();
    this.transactionStore.initialize();
    this.calculationPastStore.initialize();
    this.calculationTodayStore.initialize();
    this.scenarioStore.initialize();
    this.budgetStore.initialize();
    this.savingGoalStore.initialize();
  }

  /**
   * Rebuilds every store from the vault as it is now (re-decrypting ciphers). For changes made
   * server-side that the stores can't see on their own — e.g. deleting a team, which deletes the
   * org copies of the accounts shared into it. Sync first so the cipher cache is current.
   *
   * Built from the same steps as startup (decrypt, load models, initialise every store) rather
   * than calling the startup sequence itself, which is bootstrap's and changes with it. The
   * stores are only cleared once the vault has decrypted, so a failure leaves the current data in
   * place, and the overlay is always lifted.
   */
  async reloadFromVault(): Promise<void> {
    this.appState.startProcess("store-initialisation", "Initializing GLOSS Vault...");
    try {
      const data = await this.daliService.initialise();
      this.clearStores();
      this.loadModels(data);
      await this.initializeStores();
    } finally {
      this.appState.endProcess("store-initialisation");
    }
  }

  clearStores(): void {
    this.transactionStore.clearStore();
    this.calculationPastStore.clearStore();
    this.calculationTodayStore.clearStore();
    this.scenarioStore.clearStore();
    this.dataPluginStore.clearStore();
    this.importStore.clearStore();
    this.userStore.clearStore();
    this.budgetStore.clearStore();
    this.savingGoalStore.clearStore();
  }

  exportVaultData(): Map<VaultCodeEnum, StoreData[]> {
    return this.exportedVault;
  }

  private getModelName(vaultCodeEnum: VaultCodeEnum): StoreModelNames {
    return StoreModelNameByVaultCode[vaultCodeEnum] ?? "StoreModel";
  }

  private getStore(vaultCode: VaultCodeEnum): ModelStoreAbstraction {
    switch (vaultCode) {
      case VaultCodeEnum.Book:
        return this.userStore;
      case VaultCodeEnum.Institution:
        return this.userStore;
      case VaultCodeEnum.Category:
        return this.userStore;
      case VaultCodeEnum.Classification:
        return this.userStore;
      case VaultCodeEnum.ReferenceData:
        return this.transactionStore;
      case VaultCodeEnum.Estimate:
        return this.scenarioStore;
      case VaultCodeEnum.Symbol:
        return this.transactionStore;
      case VaultCodeEnum.Transaction:
        return this.transactionStore;
      case VaultCodeEnum.SourceTransaction:
        return this.importStore;
      case VaultCodeEnum.SourceBalance:
        return this.importStore;
      case VaultCodeEnum.SourceBook:
        return this.importStore;
      case VaultCodeEnum.Preference:
        return this.userStore;
      case VaultCodeEnum.EstimateAction:
        return this.scenarioStore;
      case VaultCodeEnum.Scenario:
        return this.scenarioStore;
      case VaultCodeEnum.ScenarioGroup:
        return this.scenarioStore;
      case VaultCodeEnum.Budget:
        return this.budgetStore;
      case VaultCodeEnum.SavingGoal:
        return this.savingGoalStore;
      case VaultCodeEnum.SourceCategory:
        return this.importStore;
      case VaultCodeEnum.Connector:
        return this.userStore;
      case VaultCodeEnum.VaultFile:
        return this.userStore;
      case VaultCodeEnum.Wizard:
        return this.userStore;
      case VaultCodeEnum.Key:
        return this.userStore;
      case VaultCodeEnum.Plugin:
        return this.userStore;
      case VaultCodeEnum.SyncStatus:
        return this.importStore;
      default:
        throw new Error("StoreLoaderService: Store Not Implemented.");
    }
  }
}
